/**
 * REGION-EXCLUSIVE species — the ones Pokémon GO only spawns in part of the
 * world, which is a different thing entirely from a REGIONAL FORM.
 *
 * The two get called the same word and they are not related:
 *   region-exclusive — Kangaskhan spawns in Australia and nowhere else. You
 *                      cannot catch one at home at any price; it is a trade or
 *                      a flight. This file.
 *   regional FORM    — Alolan Raichu, Galarian Zigzagoon, Hisuian Sneasel.
 *                      Available worldwide, just a different form of a species
 *                      you already meet. That is formCatalog.ts.
 *
 * WHY THIS IS A HAND-MAINTAINED TABLE, and how to keep it honest.
 *
 * There is no machine-readable source for it. Region locks are server-side geo
 * configuration: they are not in GO's Game Master, PokeAPI has no concept of
 * them, and Niantic moves them without announcement (Plusle and Minun were a
 * split pair for years and are global now; Lunatone and Solrock have swapped
 * hemispheres several times). shared/dexRarity.ts says the same thing and
 * declines to score regionals for exactly this reason.
 *
 * So this is a curated list, and it is meant to be edited by hand when the game
 * changes. Every entry carries the region it is locked to, so the whole table
 * can be audited by reading it. Adding or removing one species is a one-line
 * change and the tests do not pin the membership, only the shape.
 *
 * WHAT IS DELIBERATELY NOT HERE: species whose FORMS are region-split but which
 * you can catch anywhere in some form — Shellos, Basculin, Flabébé, Furfrou.
 * Nothing about those blocks a dex entry, so calling them region-locked would
 * put a species on the list that the trainer beside you in the park just
 * caught. They are listed in FORM_ONLY_REGION_SPLIT below so the distinction is
 * recorded rather than silently made, and moving one across is a one-line edit.
 * Oricorio IS here: all four of its styles are region-locked, so there is no
 * form of it you can catch outside its region.
 */

/**
 * dex id -> the region it is locked to, as a trainer would say it.
 *
 * The value is documentation, not logic: nothing keys off the string, and it is
 * here so that auditing the table means reading it rather than looking up 40
 * dex numbers. Sorted by dex number.
 *
 * An entry's EVOLUTIONS are listed too when the regional base is their only
 * source (the elemental monkeys): Simisage is as unreachable outside
 * Asia-Pacific as Pansage is, so leaving it off would make "Region locked"
 * understate the gap. Evolutions reachable another way are NOT
 * here — Sirfetch'd comes from GALARIAN Farfetch'd, which is global, so it is
 * not locked even though plain Farfetch'd is.
 */
export const REGION_EXCLUSIVE_SPECIES: Readonly<Record<number, string>> = Object.freeze({
  // ── Kanto ──
  83: "Asia (Japan, South Korea)",           // Farfetch'd
  115: "Australia, New Zealand",             // Kangaskhan
  122: "Europe",                             // Mr. Mime
  128: "North America",                      // Tauros
  // ── Johto ──
  214: "Mexico, Central and South America",  // Heracross
  222: "The tropics",                        // Corsola
  // ── Hoenn ──
  313: "Europe, Asia, Australia",            // Volbeat
  314: "The Americas, Africa",               // Illumise
  324: "India, South Asia",                  // Torkoal
  335: "Western hemisphere, Africa",         // Zangoose
  336: "Eastern hemisphere",                 // Seviper
  337: "Rotates hemisphere",                 // Lunatone
  338: "Rotates hemisphere",                 // Solrock
  357: "Africa, the Levant, southern Spain", // Tropius
  369: "New Zealand, the South Pacific",     // Relicanth
  // ── Sinnoh ──
  417: "Canada, Alaska, Russia",             // Pachirisu
  439: "Europe (eggs)",                      // Mime Jr.
  441: "Southern hemisphere",                // Chatot
  455: "South-eastern USA",                  // Carnivine
  480: "Asia-Pacific",                       // Uxie
  481: "Europe, Middle East, Africa, India", // Mesprit
  482: "The Americas, Greenland",            // Azelf
  // ── Unova ──
  511: "Asia-Pacific",                       // Pansage
  512: "Asia-Pacific (evolve Pansage)",      // Simisage
  513: "Europe, Middle East, Africa",        // Pansear
  514: "Europe, Middle East, Africa (evolve Pansear)", // Simisear
  515: "The Americas, Greenland",            // Panpour
  516: "The Americas, Greenland (evolve Panpour)",     // Simipour
  538: "The Americas, Africa",               // Throh
  539: "Europe, Asia, Oceania",              // Sawk
  556: "Central and South America, the Caribbean", // Maractus
  561: "Egypt, Greece",                      // Sigilyph
  626: "New York City",                      // Bouffalant
  631: "Eastern hemisphere",                 // Heatmor
  632: "Western hemisphere",                 // Durant
  // ── Kalos ──
  701: "Mexico",                             // Hawlucha
  707: "France, southern England",           // Klefki
  // ── Alola ──
  741: "Every style is region-locked",       // Oricorio
  764: "Hawaii",                             // Comfey
  794: "The Americas",                       // Buzzwole
  795: "Europe, Middle East, Africa, India", // Pheromosa
});

/**
 * Species whose FORMS are split by region but which you can catch SOMEWHERE in
 * every region, so no dex entry is out of reach and they are not in the filter.
 *
 * Kept as a named export rather than a comment so the judgement is visible and
 * reversible: if the owner decides a form split is worth surfacing, moving an
 * id from here into REGION_EXCLUSIVE_SPECIES is the whole change.
 */
export const FORM_ONLY_REGION_SPLIT: readonly number[] = Object.freeze([
  422, // Shellos — West Sea / East Sea by hemisphere
  423, // Gastrodon — as Shellos
  550, // Basculin — red-striped / blue-striped by hemisphere
  669, // Flabébé — flower colour by region (and Floette / Florges above it)
  670,
  671,
  676, // Furfrou — some trims only change in their own region
]);

const EXCLUSIVE_IDS = new Set<number>(
  Object.keys(REGION_EXCLUSIVE_SPECIES).map(Number),
);

/** True when Pokémon GO only spawns this species in part of the world. */
export function isRegionExclusive(dexId: number): boolean {
  return EXCLUSIVE_IDS.has(dexId);
}

/** Where a region-exclusive species is locked to, or null if it is not one. */
export function regionExclusiveArea(dexId: number): string | null {
  return REGION_EXCLUSIVE_SPECIES[dexId] ?? null;
}

/** How many species the roster currently holds. */
export const REGION_EXCLUSIVE_COUNT = EXCLUSIVE_IDS.size;
